import React from 'react';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {FiPhone, FiMapPin, FiClock, FiMail, FiMessageCircle} = FiIcons;

const Contact = () => {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="min-h-screen">
      {/* Hero Section - Light yellow header with red circles */}
      <section className="bg-gradient-to-br from-[#ffeb3b] via-[#fff59d] to-[#ffecb3] text-black py-20 relative overflow-hidden">
        {/* Red circle templates */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            initial={{ x: -100, opacity: 0 }}
            animate={{ x: 100, opacity: 0.3 }}
            transition={{ duration: 20, repeat: Infinity, repeatType: "reverse" }}
            className="absolute top-10 left-10 w-16 h-16 bg-[#ff8080] rounded-full blur-xl"
          />
          <motion.div
            initial={{ y: -50, opacity: 0 }}
            animate={{ y: 50, opacity: 0.3 }}
            transition={{ duration: 15, repeat: Infinity, repeatType: "reverse" }}
            className="absolute bottom-20 right-20 w-20 h-20 bg-[#ffcc80] rounded-full blur-xl"
          />
          <motion.div
            initial={{ rotate: 0, scale: 0.8 }}
            animate={{ rotate: 360, scale: 1 }}
            transition={{ duration: 25, repeat: Infinity, repeatType: "reverse" }}
            className="absolute top-1/2 left-1/4 w-24 h-24 bg-[#ff9999] rounded-full opacity-20 blur-2xl"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h1 
            initial={{ y: 30, opacity: 0 }} 
            animate={{ y: 0, opacity: 1 }} 
            transition={{ delay: 0.2 }} 
            className="text-4xl md:text-5xl font-bold mb-6"
          >
            Contact Us
          </motion.h1>
          <motion.p 
            initial={{ y: 30, opacity: 0 }} 
            animate={{ y: 0, opacity: 1 }} 
            transition={{ delay: 0.4 }} 
            className="text-xl max-w-3xl mx-auto"
          >
            Get in touch with us for orders, inquiries, or just to say hello
          </motion.p>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Details */}
            <motion.div initial={{ x: -30, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.3 }}>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Get In Touch</h2>
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="bg-red-100 p-3 rounded-lg mr-4">
                    <SafeIcon icon={FiPhone} className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">Phone</h3>
                    <p className="text-gray-600">+14164659103</p>
                    <p className="text-sm text-gray-500">Call us for orders and inquiries</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="bg-red-100 p-3 rounded-lg mr-4">
                    <SafeIcon icon={FiMapPin} className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">Address</h3>
                    <p className="text-gray-600">353 Broadview Ave</p>
                    <p className="text-gray-600">Toronto, ON M4M 2H1, Canada</p>
                    <a href="https://maps.app.goo.gl/5UrwfXYobJPutNCF6" target="_blank" rel="noopener noreferrer" className="text-red-600 hover:text-red-700 text-sm font-medium">
                      Get Directions →
                    </a>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="bg-red-100 p-3 rounded-lg mr-4">
                    <SafeIcon icon={FiClock} className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">Hours</h3>
                    <div className="text-gray-600">
                      <p>Monday - Sunday</p>
                      <p className="font-medium">7:00 AM - 8:00 PM</p>
                    </div>
                    <p className="text-sm text-gray-500">Open every day</p>
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="mt-8 space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">Quick Actions</h3>
                <div className="flex flex-col sm:flex-row gap-4">
                  <a href="tel:+14164659103" className="flex items-center justify-center bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 transition-colors">
                    <SafeIcon icon={FiPhone} className="w-5 h-5 mr-2" />
                    Call Now
                  </a>
                  <a href="https://maps.app.goo.gl/5UrwfXYobJPutNCF6" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center border-2 border-red-600 text-red-600 px-6 py-3 rounded-lg hover:bg-red-50 transition-colors">
                    <SafeIcon icon={FiMapPin} className="w-5 h-5 mr-2" />
                    Directions
                  </a>
                </div>
              </div>
            </motion.div>

            {/* Map */}
            <motion.div initial={{ x: 30, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.5 }} className="bg-gray-100 rounded-lg overflow-hidden shadow-lg">
              <iframe 
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2886.1821802064933!2d-79.35236830010024!3d43.66518079358563!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89d4cb68ab81f741%3A0x7b3d28dc5a39eba9!2sTung%20Hing%20Bakery!5e0!3m2!1sen!2s!4v1764947173039!5m2!1sen!2s" 
                width="100%" 
                height="450" 
                style={{ border: 0 }} 
                allowFullScreen="" 
                loading="lazy" 
                referrerPolicy="no-referrer-when-downgrade"
                title="Tung Hing Bakery Location"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-gray-50 relative overflow-hidden">
        {/* Moving elements - 30% lighter */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            initial={{ x: 100, opacity: 0 }}
            animate={{ x: -100, opacity: 0.3 }}
            transition={{ duration: 18, repeat: Infinity, repeatType: "reverse" }}
            className="absolute top-1/4 right-10 w-14 h-14 bg-[#ff9999] rounded-full blur-xl"
          />
          <motion.div
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: -80, opacity: 0.3 }}
            transition={{ duration: 12, repeat: Infinity, repeatType: "reverse" }}
            className="absolute bottom-10 left-1/3 w-18 h-18 bg-[#ff8080] rounded-full blur-xl"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
            <p className="text-gray-600">Common questions about our bakery and services</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Do you take custom orders?</h3>
              <p className="text-gray-600">
                Yes, we accept custom orders for special occasions. Please call us at least 24-48 hours in advance to discuss your requirements.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">What are your most popular items?</h3>
              <p className="text-gray-600">
                Our egg tarts, steamed buns, and traditional Chinese pastries are customer favorites. All items are made fresh daily.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Do you offer catering services?</h3>
              <p className="text-gray-600">
                Yes, we provide catering services for events and gatherings. Contact us to discuss your catering needs and get a quote.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Is parking available?</h3>
              <p className="text-gray-600">
                Yes, there is street parking available near our location on Broadview Avenue. We're also accessible by public transportation.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section - Light yellow header with red circles */}
      <section className="py-16 bg-gradient-to-br from-[#ffeb3b] via-[#fff59d] to-[#ffecb3] text-black relative overflow-hidden">
        {/* Red circle templates */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            initial={{ scale: 0.5, rotate: 0 }}
            animate={{ scale: 1, rotate: 180 }}
            transition={{ duration: 20, repeat: Infinity, repeatType: "reverse" }}
            className="absolute top-10 left-10 w-32 h-32 bg-[#ff6666] rounded-full opacity-20 blur-3xl"
          />
          <motion.div
            initial={{ x: 50, opacity: 0 }}
            animate={{ x: -50, opacity: 0.3 }}
            transition={{ duration: 15, repeat: Infinity, repeatType: "reverse" }}
            className="absolute bottom-20 right-1/4 w-16 h-16 bg-[#ffcc80] rounded-full blur-xl"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Visit?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">Come experience the authentic taste of our fresh baked goods and traditional pastries.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:+14164659103" className="bg-red-500 text-white px-8 py-3 rounded-lg font-semibold hover:bg-red-600 transition-colors">
              Call to Order
            </a>
            <a href="https://maps.app.goo.gl/5UrwfXYobJPutNCF6" target="_blank" rel="noopener noreferrer" className="border-2 border-red-600 text-red-600 px-8 py-3 rounded-lg font-semibold hover:bg-red-600 hover:text-white transition-colors">
              Get Directions
            </a>
          </div>
        </div>
      </section>
    </motion.div>
  );
};

export default Contact;